// Server-clock correction for envelope times (WEB_PLAN.md §3.6, §7.1 "Clock skew").
//
// `modified_at` is the edit time phones use as the recency tie-break when two edits to one field meet
// (`localTime > item.modifiedAt`, SyncReplica.apply). A browser whose clock runs ahead would win every such merge
// on every iPhone, so the web stamps edits with the server's time instead: the offset comes from the HTTP `Date`
// header of our own Supabase responses (PostgREST lists `Date` in Access-Control-Expose-Headers, so it is readable
// cross-origin; when it is not, the offset simply stays 0).
//
// `Date` has one-second resolution and is stamped before the response reaches us, so `date - receivedAt` is a
// lower bound of (server now - local now). Using the largest recent lower bound keeps stamps as close to server time
// as we can tell while guaranteeing they are never in the server's future.

const WINDOW = 8;

let samples: number[] = [];
let offsetMs = 0;
let lastIssued = 0;

/** Records one response's `Date` header (`receivedAt` = local ms when the response arrived). */
export function recordServerDate(dateHeader: string | null | undefined, receivedAt: number = Date.now()): void {
  if (!dateHeader) return;
  const server = Date.parse(dateHeader);
  if (!Number.isFinite(server)) return;
  samples.push(server - receivedAt);
  if (samples.length > WINDOW) samples = samples.slice(-WINDOW);
  offsetMs = Math.max(...samples);
}

/** Current estimate of (server clock - local clock), in ms (a lower bound; 0 until a response was seen). */
export function serverOffsetMs(): number {
  return offsetMs;
}

/**
 * Now, in server time, for `modified_at` (never in the server's future; never earlier than a stamp already issued,
 * so successive edits on this tab keep their order even when the offset estimate moves).
 */
export function nowForEnvelope(localNow: number = Date.now()): Date {
  const t = Math.max(Math.floor(localNow + offsetMs), lastIssued);
  lastIssued = t;
  return new Date(t);
}

/** Tests only: forget every sample. */
export function resetServerClock(): void {
  samples = [];
  offsetMs = 0;
  lastIssued = 0;
}

/**
 * `fetch` for the Supabase client (`createClient(…, { global: { fetch: clockFetch } })`): a pass-through that records
 * the `Date` header of every response. Resolves `globalThis.fetch` per call so tests and polyfills can swap it.
 */
export const clockFetch: typeof fetch = async (input, init) => {
  const res = await globalThis.fetch(input, init);
  try {
    recordServerDate(res.headers.get('date'), Date.now());
  } catch {
    // Opaque or exotic responses: no sample.
  }
  return res;
};
